import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import {
    GET_ACCOUNT_DELIVERY_DRIVER_LISTING,
    AccountDeliveryDriverListingResponse,
    AccountDeliveryDriverFiltersTypeInput
} from "../graphql/queries/getAccountDeliveryDriverListing";
import { GET_CENTRAL_DELIVERY_DRIVER_LISTING, CentralDeliveryDriverListingResponse } from "../graphql/queries/getCentralDeliveryDriverListing";
import { GET_WMS_DELIVERY_DRIVER_LISTING, WmsDeliveryDriverListingResponse } from "../graphql/queries/getWmsDeliveryDriverListing";
import { AccountDeliveryDriverType } from "../types/AccountDeliveryDriverType";

export const ACCOUNTS_PAGE_MODE = {
    CENTRAL: 'central',
    WMS: 'wms',
    DSP: 'dsp',
} as const;

export type AccountsPageMode = typeof ACCOUNTS_PAGE_MODE[keyof typeof ACCOUNTS_PAGE_MODE];

export interface AccountDeliveryDriverQueryResult {
    accountDeliveryDrivers: AccountDeliveryDriverType[];
    loading: boolean;
    refetch: (variables?: any) => Promise<any>;
    totalElements: number;
    buildQueryVariables: () => any;
}

const toSafePositiveInt = (value: unknown, fallback: number): number => {
    const num = Number(value);
    if (!Number.isFinite(num)) return fallback;
    const intVal = Math.floor(num);
    return intVal > 0 ? intVal : fallback;
};

export const useAccountDeliveryDriverQuery = (
    filters: AccountDeliveryDriverFiltersTypeInput,
    currentPage: number,
    pageSize: number,
    mode?: AccountsPageMode
): AccountDeliveryDriverQueryResult => {
    const buildQueryVariables = useMemo(() => ({
        filter: { ...filters },
        pageRequest: {
            page: currentPage,
            size: pageSize,
            sortBy: "id",
            sortDirection: "DESC"
        }
    }), [filters, currentPage, pageSize]);

    // 兼容处理：central 后端接口迁移完成前，central 入口临时复用 dsp 的司机列表接口，
    // 待 central 迁移完成后，这里改回 skip: mode !== ACCOUNTS_PAGE_MODE.DSP，
    // 并将下方 GET_CENTRAL_DELIVERY_DRIVER_LISTING 的 skip 改回 mode !== ACCOUNTS_PAGE_MODE.CENTRAL，
    // 同时把 modeResultMap 里 CENTRAL 分支改回使用 centralData/centralLoading/centralRefetch。
    const { data: dspData, loading: dspLoading, refetch: dspRefetch } = useQuery<AccountDeliveryDriverListingResponse>(
        GET_ACCOUNT_DELIVERY_DRIVER_LISTING,
        {
            variables: buildQueryVariables,
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            skip: mode !== ACCOUNTS_PAGE_MODE.DSP && mode !== ACCOUNTS_PAGE_MODE.CENTRAL
        }
    );

    // 兼容处理：central 迁移完成前暂不查询该接口，见上方注释
    const { data: centralData, loading: centralLoading, refetch: centralRefetch } = useQuery<CentralDeliveryDriverListingResponse>(
        GET_CENTRAL_DELIVERY_DRIVER_LISTING,
        {
            variables: buildQueryVariables,
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            skip: true
        }
    );

    const { data: wmsData, loading: wmsLoading, refetch: wmsRefetch } = useQuery<WmsDeliveryDriverListingResponse>(
        GET_WMS_DELIVERY_DRIVER_LISTING,
        {
            variables: buildQueryVariables,
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            skip: mode !== ACCOUNTS_PAGE_MODE.WMS || !filters.warehouseId
        }
    );

    const modeResultMap = {
        // 兼容处理：central 迁移完成前临时复用 dsp 的查询结果，迁移后改回 centralData?.getCentralDeliveryDriverListing / centralLoading / centralRefetch
        [ACCOUNTS_PAGE_MODE.CENTRAL]: { listing: dspData?.getAccountDeliveryDriverListing,     loading: dspLoading,     refetch: dspRefetch     },
        // [ACCOUNTS_PAGE_MODE.CENTRAL]: { listing: centralData?.getCentralDeliveryDriverListing, loading: centralLoading, refetch: centralRefetch },
        [ACCOUNTS_PAGE_MODE.WMS]:     { listing: wmsData?.getWmsDeliveryDriverListing,         loading: wmsLoading,     refetch: wmsRefetch     },
        [ACCOUNTS_PAGE_MODE.DSP]:     { listing: dspData?.getAccountDeliveryDriverListing,     loading: dspLoading,     refetch: dspRefetch     },
    };
    const { listing, loading, refetch } = mode
        ? modeResultMap[mode]
        : { listing: undefined, loading: false, refetch: async () => ({}) };

    const accountDeliveryDrivers = listing?.content || [];

    // Prefer backend totalElements. If backend doesn't populate it reliably, fall back to totalPages/hasNext.
    const rawTotalElements = Number(listing?.totalElements);
    const safePageSize = toSafePositiveInt(pageSize, 10);
    const safeCurrentPage = toSafePositiveInt(currentPage, 1);
    const totalElements =
        Number.isFinite(rawTotalElements) && rawTotalElements > 0
            ? rawTotalElements
            : (
                listing?.totalPages && listing.totalPages > 0
                    ? listing.totalPages * safePageSize
                    : (listing?.hasNext ? safeCurrentPage * safePageSize + 1 : accountDeliveryDrivers.length)
            );

    return {
        accountDeliveryDrivers,
        loading,
        refetch,
        totalElements,
        buildQueryVariables: () => buildQueryVariables
    };
};

